import React from 'react';
import {
  LayoutDashboard,
  Users,
  Shield,
  FileText,
  BarChart3,
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Sparkles,
  Brain,
  Clock,
  User,
  Rss,
  Ticket,
  Mail,
} from 'lucide-react';
import clsx from 'clsx';
import { useRecentEntities } from '../contexts/RecentEntitiesContext';
import { RecentEntity } from '../types/RecentEntities';
import {
  ClientUserInterface,
  ClientBelief,
  ClientThoughtWithAuthor,
  ClientFeed,
} from '../Admin-ClientLibrary';
import './Sidebar.css';

interface SidebarProps {
  collapsed: boolean;
  currentPath: string;
  onNavigate: (path: string) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed, currentPath, onNavigate }) => {
  const { recentEntities } = useRecentEntities();

  const menuSections = [
    {
      title: 'Dashboard',
      items: [
        {
          path: '/dashboard',
          label: 'Overview',
          icon: LayoutDashboard,
          isActive: currentPath === '/' || currentPath === '/dashboard',
        },
        {
          path: '/analytics',
          label: 'Analytics',
          icon: BarChart3,
          isActive: currentPath === '/analytics',
        },
        {
          path: '/settings',
          label: 'Settings',
          icon: Settings,
          isActive: currentPath === '/settings',
        },
      ],
    },
    {
      title: 'Entities',
      items: [
        {
          path: '/users',
          label: 'Users',
          icon: Users,
          isActive: currentPath === '/users',
        },
        {
          path: '/beliefs',
          label: 'Beliefs',
          icon: Shield,
          isActive: currentPath === '/beliefs',
        },
        {
          path: '/supportingbeliefs',
          label: 'Supporting Beliefs',
          icon: Shield,
          isActive: currentPath === '/supportingbeliefs',
        },
        {
          path: '/thoughts',
          label: 'Thoughts',
          icon: FileText,
          isActive: currentPath === '/thoughts',
        },
        {
          path: '/feeds',
          label: 'Feeds',
          icon: Rss,
          isActive: currentPath === '/feeds',
        },
      ],
    },
    {
      title: 'AI Wizards',
      items: [
        {
          path: '/ai-supporting-beliefs',
          label: 'AI Supporting Beliefs',
          icon: Brain,
          isActive: currentPath === '/ai-supporting-beliefs',
        },
      ],
    },
    {
      title: 'Billing',
      items: [
        {
          path: '/coupons',
          label: 'Coupons',
          icon: Ticket,
          isActive: currentPath === '/coupons' || currentPath.startsWith('/coupons/'),
        },
        {
          path: '/invites',
          label: 'Invites',
          icon: Mail,
          isActive: currentPath === '/invites' || currentPath.startsWith('/invites/'),
        },
      ],
    },
  ];

  const getEntityIcon = (type: string) => {
    switch (type) {
      case 'user':
        return User;
      case 'belief':
        return Shield;
      case 'thought':
        return FileText;
      case 'feed':
        return Rss;
      default:
        return FileText;
    }
  };

  const getEntityDisplayText = (entity: RecentEntity): string => {
    switch (entity.type) {
      case 'user':
        const user = entity.data as ClientUserInterface;
        return user.name || `User ${entity.id}`;
      case 'belief':
        const belief = entity.data as ClientBelief;
        return `Belief ${entity.id} (${belief.position?.toUpperCase() || 'UNKNOWN'})`;
      case 'thought':
        const thought = entity.data as ClientThoughtWithAuthor;
        const text = thought.text || '';
        return text.length > 30 ? `${text.substring(0, 30)}...` : text;
      case 'feed':
        const feed = entity.data as ClientFeed;
        return feed.title || `Feed ${entity.id}`;
      default:
        return `Entity ${entity.id}`;
    }
  };

  const getEntityPath = (entity: RecentEntity): string => {
    switch (entity.type) {
      case 'user':
        return `/users/${entity.id}`;
      case 'belief':
        return `/belief/${entity.id}`;
      case 'thought':
        return `/thoughts/${entity.id}`;
      case 'feed':
        return `/feeds/${entity.id}`;
      default:
        return '/';
    }
  };

  const handleLogout = () => {
    // Add logout logic here
    console.log('Logout clicked');
  };

  return (
    <div className={clsx('sidebar', { 'sidebar-collapsed': collapsed })}>
      <div className="sidebar-header">
        <div className="sidebar-logo">
          {!collapsed && <span className="logo-text">EPIST Admin</span>}
        </div>
      </div>

      <nav className="sidebar-nav">
        {/* Existing Menu Sections */}
        {menuSections.map((section) => (
          <div key={section.title} className="nav-section">
            {!collapsed && <div className="nav-section-title">{section.title}</div>}
            <ul className="nav-list">
              {section.items.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.path} className="nav-item">
                    <button
                      className={clsx('nav-link', { 'nav-link-active': item.isActive })}
                      onClick={() => onNavigate(item.path)}
                      title={collapsed ? item.label : ''}
                    >
                      <Icon size={20} />
                      {!collapsed && <span className="nav-label">{item.label}</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}

        {/* Recent Section - Always visible, at the bottom */}
        <div className="nav-section">
          {!collapsed && <div className="nav-section-title">Recent</div>}
          <ul className="nav-list">
            {recentEntities.slice(0, 5).map((entity) => {
              const Icon = getEntityIcon(entity.type);
              const displayText = getEntityDisplayText(entity);
              const entityPath = getEntityPath(entity);
              const isActive = currentPath === entityPath;

              return (
                <li key={`${entity.type}-${entity.id}`} className="nav-item">
                  <button
                    className={clsx('nav-link recent-link', { 'nav-link-active': isActive })}
                    onClick={() => onNavigate(entityPath)}
                    title={collapsed ? displayText : ''}
                  >
                    <Icon size={16} />
                    {!collapsed && <span className="nav-label recent-label">{displayText}</span>}
                  </button>
                </li>
              );
            })}
            {recentEntities.length === 0 && !collapsed && (
              <li className="nav-item">
                <div className="no-recent-items">
                  <span className="nav-label recent-label">No recent items</span>
                </div>
              </li>
            )}
          </ul>
        </div>
      </nav>

      <div className="sidebar-footer">
        <button className="nav-link logout-button" onClick={handleLogout}>
          <LogOut size={20} />
          {!collapsed && <span className="nav-label">Logout</span>}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
